///
/// Copyright (c) 2017-present, Siklu Ltd.
/// All rights reserved.
///

import { Component, HostListener, OnDestroy, OnInit } from '@angular/core';
import { ProjectWindowsPopupService } from '../project-windows-popup.service';
import { GtthHistoryService } from '../../logical-topology/engine/history-service';
import { AttachedFileDTO } from '../../../generated/shared-types.model';
import { Subscription } from 'rxjs';
import { GtthDataUtils } from '../../../services/data-util.service';
import { GtthImportVerificationCommand } from '../../logical-topology/engine/commands/project/import-verification-command';
import { ActiveRestorableWindow } from '../active-restorable-window';
import { GtthImportNodesService } from './import-nodes.service';
import { GtthDuplicatedNodeFeatureDto } from './node-duplications-resolver/model/duplicated-node-feature-dto.model';
import { GtthUpdateMainConfigCommand } from '../../logical-topology/engine/commands/project/update-main-config-command';
import { MatDialogRef } from '@angular/material/dialog';

@Component({
    selector: 'winde-import-nodes-modal',
    templateUrl: './import-nodes-modal.component.html',
    styleUrls: ['./import-nodes-modal.scss'],
    providers: [GtthImportNodesService],
    standalone: false
})
export class GtthImportNodesComponent extends ActiveRestorableWindow implements OnInit, OnDestroy {
    public isFileParsed: boolean;
    public isResolvingNotAllowed: boolean;

    public hasNodeDuplications: boolean;
    public isDuplicationsResolved: boolean;
    public verificationFinished: boolean;
    public nothingToImport: boolean;

    public totalNodesCount: number;
    public totalLinksCount: number;

    public nodesDuplicationDistance = 5;
    public nodesDuplicationMap: Map<number, GtthDuplicatedNodeFeatureDto[]>;
    public fileDuplications: GtthDuplicatedNodeFeatureDto[][] = [];

    public isCreateNewNodes = true;
    public createNodesCount: number;
    public isUpdateStandalone = true;
    public updateStandaloneCount: number;
    public isUpdateConnectedNodes = true;
    public updateConnectedCount: number;
    public losesToRemoveCount: number;

    public isCreateLoses: boolean;
    public isCreateLinks: boolean;
    public createLosesCount: number;
    public createLinksCount: number;

    public fileInp;
    public file: File;
    public layerModeWillBeEnabled = false;
    public topologyImported = false;
    public isImporting = false;
    private lastParsedFile: File;
    private editingLockerSubscriber: Subscription;
    private readonly FEET_MULTIPLIER = 0.3048;

    constructor(
        public dialogRef: MatDialogRef<GtthImportNodesComponent>,
        public readonly historyService: GtthHistoryService,
        public readonly importNodesService: GtthImportNodesService
    ) {
        super();
    }

    ngOnInit() {
        this.clearParsingInfo();
        this.clearVerificationResult();
        this.editingLockerSubscriber = this.importNodesService.editingLocker.subscribe((node: GtthDuplicatedNodeFeatureDto) => {
            this.isResolvingNotAllowed = node !== null;
        });

        if (this.historyService.logicalTopology.productUnits === 'feet') {
            this.nodesDuplicationDistance = Math.floor(this.nodesDuplicationDistance / this.FEET_MULTIPLIER);
        }
    }

    ngOnDestroy() {
        if (this.editingLockerSubscriber) {
            this.editingLockerSubscriber.unsubscribe();
            this.editingLockerSubscriber = null;
        }
    }

    fileChange(event) {
        let previousFileName;
        if (this.file) {
            previousFileName = this.file.name;
        }

        const fileList: FileList = event.target.files;
        if (fileList.length > 0) {
            this.file = fileList.item(0);
        }

        if (previousFileName !== this.file.name) {
            this.clearParsingInfo();
            this.clearVerificationResult();
        }
    }

    downloadTemplate() {
        if (!this.file) {
            return;
        }

        if (this.layerModeWillBeEnabled && !this.topologyImported) {
            this.historyService.logicalTopology.layersModeEnabled = false;
        }

        if (this.lastParsedFile && this.lastParsedFile.name === this.file.name) {
            this.handleImportedEntities();
            return;
        }

        this.isImporting = true;

        GtthDataUtils.toBase64(this.file, (base64Data) => {
            const attachedFile: AttachedFileDTO = {
                fileName: this.file.name,
                file: base64Data,
                contentType: this.file.type
            };

            const importVerificationCommand = this.historyService.command(GtthImportVerificationCommand).create(attachedFile);
            this.historyService.doCommandBypassHistory(importVerificationCommand).then((result: boolean) => {
                this.isImporting = false;
                if (result) {
                    this.importNodesService.handleImportedNodes(
                        importVerificationCommand.getResultNodes(),
                        importVerificationCommand.getResultLinks()
                    );
                    this.isFileParsed = true;
                    this.lastParsedFile = this.file;
                    this.handleImportedEntities();
                }
            });
        });
    }

    allDuplicationsResolved() {
        this.importNodesService.duplicationsResolved(this.nodesDuplicationMap, this.fileDuplications);
        this.clearCounters();

        this.importNodesService.recalculateNodesByType();
        this.importNodesService.recalculateLinksByType();

        this.createNodesCount = this.importNodesService.getNewNodesCount();
        this.isCreateNewNodes = this.createNodesCount > 0;
        this.updateStandaloneCount = this.importNodesService.getStandaloneNodesCount();
        this.isUpdateStandalone = this.updateStandaloneCount > 0;
        this.updateConnectedCount = this.importNodesService.getConnectedNodesCount();
        this.isUpdateConnectedNodes = this.updateConnectedCount > 0;

        this.nodesValueChanged();

        this.nothingToImport =
            !this.isCreateNewNodes &&
            !this.isUpdateStandalone &&
            !this.isUpdateConnectedNodes &&
            this.createLosesCount === 0 &&
            this.createLinksCount === 0;
        this.verificationFinished = true;
        this.isDuplicationsResolved = true;
    }

    public nodesValueChanged(): void {
        this.createLosesCount = 0;
        this.createLinksCount = 0;

        this.importNodesService.nodesValueChanged(this.isCreateNewNodes, this.isUpdateStandalone, this.isUpdateConnectedNodes);

        this.createLosesCount = this.importNodesService.getCreatedLosesCount();
        this.createLinksCount = this.importNodesService.getCreatedLinksCount();
        this.losesToRemoveCount = this.importNodesService.getLoSesToRemoveCount();

        this.isCreateLoses = this.isCreateLoses && this.createLosesCount > 0;
        this.isCreateLinks = this.isCreateLinks && this.createLinksCount > 0;
    }

    proceedImport() {
        const customImportCommand = this.importNodesService.prepareCrudCommand(
            this.isCreateNewNodes,
            this.isUpdateStandalone,
            this.isUpdateConnectedNodes,
            this.isCreateLoses,
            this.isCreateLinks
        );

        this.historyService.doCommand(customImportCommand).then((importResult: boolean) => {
            if (this.layerModeWillBeEnabled) {
                this.historyService
                    .doCommandBypassHistory(this.historyService.command(GtthUpdateMainConfigCommand))
                    .then((updateResult: boolean) => {
                        this.historyService.projectInfoViewService.showLayers(
                            this.historyService.logicalTopology.layers,
                            this.historyService.logicalTopology.activeLayerIdx
                        );
                        this.isImporting = false;
                        this.topologyImported = true;
                        if (updateResult) {
                            this.closeModal();
                        }
                    });
            } else {
                this.isImporting = false;
                if (importResult) {
                    this.closeModal();
                }
            }
        });
    }

    closeModal() {
        if (this.layerModeWillBeEnabled && !this.topologyImported) {
            this.historyService.logicalTopology.layersModeEnabled = false;
        }

        this.dialogRef.close('cancel');
        this.fileInp = '';
        if (this.file) {
            this.file = undefined;
        }
    }

    @HostListener('document:keydown', ['$event'])
    handleKeyboardUpEvent(event: KeyboardEvent) {
        if (event.key === 'Escape') {
            this.closeModal();
        }
    }

    designRequired(): boolean {
        return false;
    }

    isRestorable(): boolean {
        return true;
    }

    private clearVerificationResult(): void {
        this.hasNodeDuplications = false;
        this.isDuplicationsResolved = false;
        this.verificationFinished = false;
        this.nothingToImport = false;
        this.totalNodesCount = 0;
        this.totalLinksCount = 0;
        this.importNodesService.clearAll();
    }

    private clearParsingInfo() {
        this.isFileParsed = false;
        this.lastParsedFile = null;
    }

    private clearCounters(): void {
        this.importNodesService.clearCounters();
        this.losesToRemoveCount = 0;
        this.isCreateLoses = true;
        this.isCreateLinks = true;
    }

    private handleImportedEntities() {
        let nodesDuplicationDistanceInMeters;

        this.importNodesService.editingLocker.next(null);
        this.importNodesService.nodeNameSynchronizer.next(null);
        this.importNodesService.nodeTypeSynchronizer.next(null);

        this.totalNodesCount = this.importNodesService.getNodesCount();
        this.totalLinksCount = this.importNodesService.getLinksCount();

        if (this.historyService.logicalTopology.productUnits === 'feet') {
            nodesDuplicationDistanceInMeters = this.nodesDuplicationDistance * this.FEET_MULTIPLIER;
        } else {
            nodesDuplicationDistanceInMeters = this.nodesDuplicationDistance;
        }

        this.nodesDuplicationMap = this.importNodesService.checkForNodesDuplications(nodesDuplicationDistanceInMeters);
        this.fileDuplications = this.importNodesService.getFileDuplicates();
        this.hasNodeDuplications = this.nodesDuplicationMap.size > 0 || this.fileDuplications.length > 0;
        this.layerModeWillBeEnabled = this.importNodesService.hasNodesWithLayers && !this.historyService.logicalTopology.layersModeEnabled;
        if (this.layerModeWillBeEnabled) {
            this.historyService.logicalTopology.enableLayersMode(this.historyService);
        }
        if (!this.hasNodeDuplications) {
            this.allDuplicationsResolved();
        }
    }
}

@Component({
    selector: 'winde-import-nodes-modal-popup',
    template: '',
    standalone: false
})
export class GtthImportNodesPopupComponent implements OnInit {
    constructor(private readonly projectWindowsPopupService: ProjectWindowsPopupService) {}

    ngOnInit() {
        this.projectWindowsPopupService.openWindow(GtthImportNodesComponent, 'xl');
    }
}
